import React, { useState, useEffect, useCallback } from 'react'
import { Calendar, Clock, Hash, Image, Send, X, AlertCircle, Loader2, Eye, EyeOff, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useProfiles, useQueues, useCreatePost, useUpdatePost } from '@/hooks/useSocialMedia'
import { useToast } from '@/hooks/use-toast'
import { format } from 'date-fns'
import type { SocialPost } from '@/hooks/useSocialMedia'
import { POST_STATUS } from '@/constants/socialPostStatus'
import GalleryImageSelector from './GalleryImageSelector'
import InstagramPreview from './InstagramPreview'
import { 
  validateSocialPost, 
  getFieldError, 
  hasFieldError,
  type ValidationError,
  type ValidationResult 
} from '@/lib/validation/socialPostValidation'
import { cn } from '@/lib/utils'

interface PostCreateFormProps {
  initialData?: Partial<SocialPost>
  onClose: () => void
  onSuccess?: () => void
}

export default function PostCreateForm({ initialData, onClose, onSuccess }: PostCreateFormProps) {
  const isFullPage = !initialData?.id // Detect if this is a new post (full page) vs edit (dialog)
  const { data: profiles = [] } = useProfiles()
  const [selectedProfileId, setSelectedProfileId] = useState(initialData?.profile_id || '')
  const { data: queues = [] } = useQueues(selectedProfileId)
  const { error: toastError } = useToast()
  
  const createPost = useCreatePost()
  const updatePost = useUpdatePost()
  
  const [formData, setFormData] = useState({
    caption: initialData?.caption || '',
    content: initialData?.content || '',
    image_urls: initialData?.image_urls || [],
    profile_id: initialData?.profile_id || '',
    queue_id: initialData?.queue_id || '',
    scheduled_time: initialData?.scheduled_time || '',
    hashtags: initialData?.hashtags || [],
    tags: initialData?.tags || [],
    status: initialData?.status || 'DRAFT' as const
  })
  
  const [showGallerySelector, setShowGallerySelector] = useState(false)
  const [newHashtag, setNewHashtag] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialData?.scheduled_time ? new Date(initialData.scheduled_time) : undefined
  )
  const [selectedTime, setSelectedTime] = useState(
    initialData?.scheduled_time ? format(new Date(initialData.scheduled_time), 'HH:mm') : '09:00'
  )
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPreview, setShowPreview] = useState(true) // Show preview by default on desktop
  const [isMobile, setIsMobile] = useState(false)

  // Get the current platform for platform-specific validation
  const currentProfile = profiles.find(p => p.id === formData.profile_id)
  const currentPlatform = currentProfile?.platform

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // Hide preview on mobile by default
      if (window.innerWidth < 768) {
        setShowPreview(false)
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const validateForm = useCallback((): ValidationResult => {
    const result = validateSocialPost(formData, currentPlatform)
    setValidationErrors(result.errors)
    return result
  }, [formData, currentPlatform])

  // Validate form on data change
  useEffect(() => {
    if (touchedFields.size > 0) {
      validateForm()
    }
  }, [formData, currentPlatform, touchedFields.size, validateForm])

  const markFieldAsTouched = (fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName))
  }

  const shouldShowError = (fieldName: string): boolean => {
    return touchedFields.has(fieldName) && hasFieldError(validationErrors, fieldName)
  }

  const getErrorMessage = (fieldName: string): string | null => {
    return touchedFields.has(fieldName) ? getFieldError(validationErrors, fieldName) : null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Prevent double submission
    if (isSubmitting) return
    
    // Touch all fields to show errors
    setTouchedFields(new Set(['caption', 'profile_id', 'scheduled_time', 'hashtags', 'image_urls']))
    
    // Validate form
    const validation = validateForm()
    if (!validation.isValid) {
      toastError('Validation Error', 'Please fix the errors before submitting')
      
      // Focus on first error field
      const firstError = validation.errors[0]
      if (firstError) {
        const fieldElement = document.querySelector(`[name="${firstError.field}"]`)
        if (fieldElement instanceof HTMLElement) {
          fieldElement.focus()
        }
      }
      return
    }
    
    setIsSubmitting(true)
    console.log('Form data before submission:', formData)
    
    const postData = {
      ...formData,
      status: formData.scheduled_time ? POST_STATUS.SCHEDULED : POST_STATUS.DRAFT
    }
    
    console.log('Post data being submitted:', postData)
    
    try {
      let result
      if (initialData?.id) {
        result = await updatePost.mutateAsync({ id: initialData.id, ...postData })
        console.log('Update result:', result)
      } else {
        result = await createPost.mutateAsync(postData)
        console.log('Create result:', result)
      }
      onSuccess?.()
      if (!isFullPage) {
        onClose()
      }
    } catch (error) {
      console.error('Failed to save post:', error)
      // Error toast is handled by the mutation hook
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const addHashtag = () => {
    if (!newHashtag || newHashtag.trim() === '') return
    
    const tag = newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`
    
    // Validate hashtag format
    const hashtagRegex = /^#[\w]+$/
    if (!hashtagRegex.test(tag)) {
      toastError('Invalid Hashtag', 'Hashtags can only contain letters, numbers, and underscores')
      return
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = formData.hashtags.some(h => h.toLowerCase() === tag.toLowerCase())
    if (isDuplicate) {
      toastError('Duplicate Hashtag', 'This hashtag already exists')
      return
    }
    
    // Check platform limits
    if (currentPlatform === 'INSTAGRAM' && formData.hashtags.length >= 30) {
      toastError('Hashtag Limit', 'Instagram allows a maximum of 30 hashtags')
      return
    }
    
    setFormData({ ...formData, hashtags: [...formData.hashtags, tag] })
    setNewHashtag('')
    markFieldAsTouched('hashtags')
  }
  
  const removeHashtag = (tag: string) => {
    setFormData({ ...formData, hashtags: formData.hashtags.filter(t => t !== tag) })
    markFieldAsTouched('hashtags')
  }
  
  
  const handleImagesSelected = (imageUrls: string[]) => {
    const newImageUrls = [...formData.image_urls, ...imageUrls.filter(url => !formData.image_urls.includes(url))]
    
    // Check Instagram limit
    if (currentPlatform === 'INSTAGRAM' && newImageUrls.length > 10) {
      toastError('Image Limit', 'Instagram allows a maximum of 10 images per post')
      return
    }
    
    setFormData({ ...formData, image_urls: newImageUrls })
    markFieldAsTouched('image_urls')
  }
  
  const removeImage = (url: string) => {
    setFormData({ ...formData, image_urls: formData.image_urls.filter(u => u !== url) })
    markFieldAsTouched('image_urls')
  }

  const reorderImages = (startIndex: number, endIndex: number) => {
    const newImageUrls = [...formData.image_urls]
    const [reorderedItem] = newImageUrls.splice(startIndex, 1)
    newImageUrls.splice(endIndex, 0, reorderedItem)
    
    setFormData({ ...formData, image_urls: newImageUrls })
    markFieldAsTouched('image_urls')
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Don't start drag if clicking on button elements
    const target = e.target as HTMLElement
    if (target.closest('button')) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    if (dragIndex !== dropIndex) {
      reorderImages(dragIndex, dropIndex)
    }
  }

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    updateScheduledTime(date, selectedTime)
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
    updateScheduledTime(selectedDate, time)
  }

  const updateScheduledTime = (date: Date | undefined, time: string) => {
    if (date && time) {
      const [hours, minutes] = time.split(':').map(Number)
      const scheduledDateTime = new Date(date)
      scheduledDateTime.setHours(hours, minutes, 0, 0)
      
      setFormData({ 
        ...formData, 
        scheduled_time: scheduledDateTime.toISOString() 
      })
    } else {
      setFormData({ 
        ...formData, 
        scheduled_time: '' 
      })
    }
    markFieldAsTouched('scheduled_time')
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">
              {initialData?.id ? 'Updating post...' : 'Creating post...'}
            </p>
          </div>
        </div>
      )}
      
      {isFullPage && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Create Social Media Post</h1>
          <p className="text-muted-foreground mt-2">
            Compose and schedule your post across multiple social media platforms
          </p>
        </div>
      )}
      
      {/* Mobile Preview Toggle */}
      {isMobile && currentPlatform === 'INSTAGRAM' && (
        <div className="mb-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="w-full"
          >
            {showPreview ? (
              <><EyeOff className="h-4 w-4 mr-2" /> Hide Preview</>
            ) : (
              <><Eye className="h-4 w-4 mr-2" /> Show Preview</>
            )}
          </Button>
        </div>
      )}
      
      {/* Mobile Preview */}
      {isMobile && showPreview && currentPlatform === 'INSTAGRAM' && (
        <div className="mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Instagram Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <InstagramPreview
                caption={formData.caption}
                images={formData.image_urls}
                hashtags={formData.hashtags}
                accountName={currentProfile?.name || 'fixmybrick'}
                accountAvatar={currentProfile?.profile_picture_url}
              />
            </CardContent>
          </Card>
        </div>
      )}
      
      <div className={cn(
        "grid gap-6",
        !isMobile && currentPlatform === 'INSTAGRAM' && showPreview ? "lg:grid-cols-[1fr,468px]" : "grid-cols-1"
      )}>
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Profile Selection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="profile" className="flex items-center gap-2">
                Social Media Profile
                <span className="text-red-500">*</span>
              </Label>
              <Select
                name="profile_id"
                value={formData.profile_id}
                onValueChange={(value) => {
                  setFormData({ ...formData, profile_id: value })
                  setSelectedProfileId(value)
                  markFieldAsTouched('profile_id')
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger 
                  className={cn(
                    shouldShowError('profile_id') && "border-red-500 focus:ring-red-500"
                  )}
                >
                  <SelectValue placeholder="Select a profile" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name} ({profile.platform})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {shouldShowError('profile_id') && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  {getErrorMessage('profile_id')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Caption */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Post Content</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="caption" className="flex items-center gap-2">
                Caption
                <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="caption"
                name="caption"
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                onBlur={() => markFieldAsTouched('caption')}
                rows={8}
                placeholder="Write your post caption... (minimum 10 characters)"
                disabled={isSubmitting}
                className={cn(
                  "resize-none",
                  shouldShowError('caption') && "border-red-500 focus:ring-red-500"
                )}
              />
              <div className="flex items-center justify-between">
                <p className={cn(
                  "text-sm",
                  currentPlatform === 'INSTAGRAM' && formData.caption.length > 2200 
                    ? "text-red-500" 
                    : "text-muted-foreground"
                )}>
                  {formData.caption.length} / {currentPlatform === 'INSTAGRAM' ? '2,200' : 
                    currentPlatform === 'TWITTER' ? '280' :
                    currentPlatform === 'LINKEDIN' ? '3,000' :
                    currentPlatform === 'FACEBOOK' ? '63,206' : '2,200'} characters
                </p>
                {currentPlatform && (
                  <Badge variant="outline">{currentPlatform}</Badge>
                )}
              </div>
              {shouldShowError('caption') && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  {getErrorMessage('caption')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Media</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Images</Label>
                  {formData.image_urls.length > 1 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Drag images to reorder them
                    </p>
                  )}
                </div>
                {currentPlatform === 'INSTAGRAM' && (
                  <span className="text-sm text-muted-foreground">
                    {formData.image_urls.length} / 10 images
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {formData.image_urls.map((url, index) => (
                  <div 
                    key={index} 
                    className="relative group cursor-move"
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent group-hover:border-primary/50 transition-colors">
                      <img
                        src={url}
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Position indicator */}
                      <Badge 
                        variant="default" 
                        className="absolute top-2 left-2 text-xs h-5 px-1.5 bg-black/70 text-white border-none"
                      >
                        {index + 1}
                      </Badge>

                      {/* Remove button */}
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          removeImage(url)
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <X className="h-3 w-3" />
                      </Button>

                      {/* Drag indicator */}
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                        <div className="bg-white/90 rounded p-1">
                          <GripVertical className="h-4 w-4 text-gray-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="aspect-square flex flex-col items-center justify-center border-dashed border-2"
                  onClick={() => setShowGallerySelector(true)}
                  disabled={isSubmitting || (currentPlatform === 'INSTAGRAM' && formData.image_urls.length >= 10)}
                >
                  <Image className="h-8 w-8 mb-2" />
                  <span className="text-xs text-center">Add from Gallery</span>
                </Button>
              </div>
              {shouldShowError('image_urls') && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  {getErrorMessage('image_urls')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Hashtags and Tags */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tags & Hashtags</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Hashtags</Label>
                <span className="text-sm text-muted-foreground">
                  {formData.hashtags.length} / {currentPlatform === 'INSTAGRAM' ? '30' : 
                    currentPlatform === 'TWITTER' ? '10' : '30'}
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={newHashtag}
                  onChange={(e) => setNewHashtag(e.target.value)}
                  placeholder="Add hashtag (letters, numbers, underscores only)"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                  disabled={isSubmitting}
                  className={cn(
                    shouldShowError('hashtags') && "border-red-500 focus:ring-red-500"
                  )}
                />
                <Button 
                  type="button" 
                  onClick={addHashtag}
                  disabled={currentPlatform === 'INSTAGRAM' && formData.hashtags.length >= 30}
                >
                  <Hash className="h-4 w-4" />
                </Button>
              </div>
              {shouldShowError('hashtags') && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4" />
                  {getErrorMessage('hashtags')}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.hashtags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-1 h-4 w-4 p-0"
                      onClick={() => removeHashtag(tag)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Publishing Options</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="queue">Queue (Optional)</Label>
            <Select
              value={formData.queue_id || 'none'}
              onValueChange={(value) => setFormData({ ...formData, queue_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a queue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No queue</SelectItem>
                {queues.map((queue) => (
                  <SelectItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Schedule (Optional)</Label>
            <div className="flex gap-2">
              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground",
                      shouldShowError('scheduled_time') && "border-red-500"
                    )}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Time Picker */}
              <Select value={selectedTime} onValueChange={handleTimeChange}>
                <SelectTrigger 
                  className={cn(
                    "w-32",
                    shouldShowError('scheduled_time') && "border-red-500"
                  )}
                >
                  <SelectValue placeholder="Time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, hour) => {
                    const timeOptions = []
                    for (let minute = 0; minute < 60; minute += 15) {
                      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                      timeOptions.push(
                        <SelectItem key={timeString} value={timeString}>
                          {timeString}
                        </SelectItem>
                      )
                    }
                    return timeOptions
                  }).flat()}
                </SelectContent>
              </Select>

              {/* Clear button */}
              {(selectedDate || formData.scheduled_time) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedDate(undefined)
                    setSelectedTime('09:00')
                    setFormData({ ...formData, scheduled_time: '' })
                    markFieldAsTouched('scheduled_time')
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {shouldShowError('scheduled_time') && (
              <div className="flex items-center gap-1 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                {getErrorMessage('scheduled_time')}
              </div>
            )}
            {formData.scheduled_time && !shouldShowError('scheduled_time') && (
              <p className="text-sm text-muted-foreground">
                <Clock className="inline h-3 w-3 mr-1" />
                Scheduled for {format(new Date(formData.scheduled_time), 'PPp')}
              </p>
            )}
          </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className={`flex ${isFullPage ? 'justify-between' : 'justify-end'} gap-2 pt-4`}>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {isFullPage && (
              <Button 
                type="submit" 
                variant="outline"
                disabled={isSubmitting || validationErrors.length > 0}
                onClick={() => setFormData(prev => ({ ...prev, status: 'DRAFT' }))}
              >
                Save as Draft
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={isSubmitting || (touchedFields.size > 0 && validationErrors.length > 0)}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {initialData?.id ? 'Updating...' : 'Creating...'}
                </>
              ) : initialData?.id ? (
                'Update Post'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Create Post
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Validation Summary */}
        {touchedFields.size > 0 && validationErrors.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-red-900">Please fix the following errors:</p>
                  <ul className="text-sm text-red-700 space-y-0.5">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </form>
        
        {/* Desktop Preview Panel */}
        {!isMobile && currentPlatform === 'INSTAGRAM' && showPreview && (
          <div className="lg:sticky lg:top-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Instagram Preview</CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(false)}
                >
                  <EyeOff className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <InstagramPreview
                  caption={formData.caption}
                  images={formData.image_urls}
                  hashtags={formData.hashtags}
                  accountName={currentProfile?.name || 'fixmybrick'}
                  accountAvatar={currentProfile?.profile_picture_url}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Gallery Image Selector */}
      {showGallerySelector && (
        <GalleryImageSelector
          onImagesSelected={handleImagesSelected}
          onClose={() => setShowGallerySelector(false)}
          maxImages={10}
        />
      )}
    </div>
  )
}